// packages/console/src/components/UPlotChart.tsx
//
// A small typed React wrapper around uPlot (no heavy third-party wrapper). It
// owns a ref'd container, constructs one uPlot instance from the given options
// and data, tears it down on unmount or option identity change, and pushes new
// data with setData when only the data changes (cheaper than a full rebuild).
// A ResizeObserver keeps the chart sized to its container so the panel stays
// responsive. The caller supplies fully-built uPlot Options (series, axes, plugins).

import { useEffect, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

export interface UPlotChartProps {
  // Options WITHOUT width/height; the wrapper measures those from the container.
  options: Omit<uPlot.Options, "width" | "height">;
  data: uPlot.AlignedData;
  // Fixed pixel height for the plot area; width tracks the container.
  height: number;
  // onCreate hands the caller the live uPlot instance after each (re)build so it
  // can drive imperative controls (e.g. zoom buttons via setScale). It fires on
  // the initial build and on every options-driven rebuild; the caller must not
  // retain the instance past the next call (a rebuild destroys the prior one).
  onCreate?: (u: uPlot) => void;
}

export function UPlotChart({ options, data, height, onCreate }: UPlotChartProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);
  // Hold the latest data so a resize-driven rebuild seeds the current series.
  const dataRef = useRef<uPlot.AlignedData>(data);
  dataRef.current = data;
  // Hold onCreate in a ref so its identity never forces a chart rebuild.
  const onCreateRef = useRef(onCreate);
  onCreateRef.current = onCreate;

  // Rebuild the instance when the options object identity or height changes.
  // The panel memoizes options so this fires on a real spec change (e.g. the
  // I/P/B toggle), not on every render.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const build = () => {
      // Destroy the prior instance, then clear the ref BEFORE constructing the
      // new one. If the uPlot constructor throws (a malformed options/data
      // combination), the ref stays null so the sibling data effect's
      // setData no-ops against a torn-down instance rather than crashing.
      plotRef.current?.destroy();
      plotRef.current = null;
      const width = Math.max(1, host.clientWidth);
      const opts: uPlot.Options = { ...options, width, height };
      plotRef.current = new uPlot(opts, dataRef.current, host);
      onCreateRef.current?.(plotRef.current);
    };
    build();

    // Track container width so the chart reflows on layout changes (side column
    // collapse, window resize). uPlot has no fluid mode, so we re-size explicitly.
    const ro = new ResizeObserver(() => {
      const p = plotRef.current;
      if (p && host.clientWidth > 0) p.setSize({ width: host.clientWidth, height });
    });
    ro.observe(host);

    return () => {
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [options, height]);

  // Push data changes without a full rebuild when the options are unchanged.
  useEffect(() => {
    plotRef.current?.setData(data);
  }, [data]);

  return <div ref={hostRef} style={{ width: "100%" }} />;
}
